import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { ColumnDef } from '@tanstack/react-table';
import { Trash2, PauseCircle, AlignLeft } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable } from '@/components/glitch-ui/data-table';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FieldError } from '@/components/glitch-ui/field-error';

import { cn, containsEmoji, getFunnelStageLabel, isBrandCampaign } from '@/lib/utils';
import { formatCompactNumber } from '@/i18n/formatters';
import {
  CampaignData,
  Description,
  FunnelStage,
  CAMPAIGN_STATE,
  HeadlinePerformanceLabel,
  DescriptionWithMetrics,
  CampaignDescriptionMetricsResponse,
  AdFunnelStage,
} from '@/lib/types';

const PERFORMANCE_BADGE_STYLES: Record<HeadlinePerformanceLabel, string> = {
  BEST: 'bg-green-100 text-green-800',
  GOOD: 'bg-purple-50 text-purple-600',
  LOW: 'bg-red-100 text-red-800',
  LEARNING: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-gray-100 text-gray-900',
};

function PerformanceBadge({ label }: { label: HeadlinePerformanceLabel | null }) {
  if (!label) return null;
  const displayLabel = label.charAt(0) + label.slice(1).toLowerCase();
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', PERFORMANCE_BADGE_STYLES[label])}>
      {displayLabel}
    </span>
  );
}

type DescriptionRow = {
  description: Description;
  metrics?: DescriptionWithMetrics;
  isTopPerformer: boolean;
};

// #4625-followup: monotonic counter for stamping temp ids on unsaved
// descriptions. Negative so it can't collide with backend-assigned ids.
let nextTempDescriptionId = -1;
const newTempDescriptionId = () => nextTempDescriptionId--;

type DescriptionManagementProps = {
  descriptions: Description[];
  setDescriptions: React.Dispatch<React.SetStateAction<Description[]>>;
  disabled?: boolean;
  campaign: CampaignData;
  activeTab?: FunnelStage;
  onTabChange?: (tab: FunnelStage) => void;
  campaignId?: number;
  metricsData?: CampaignDescriptionMetricsResponse;
  metricsLoading?: boolean;
  funnelStages?: AdFunnelStage[];
  showAddModal?: boolean;
  onAddModalClose?: () => void;
};

export function DescriptionManagement({
  descriptions,
  setDescriptions: _setDescriptions,
  disabled = false,
  campaign,
  activeTab: externalActiveTab,
  onTabChange,
  metricsData,
  metricsLoading = false,
  funnelStages: funnelStageData,
  showAddModal,
  onAddModalClose,
}: DescriptionManagementProps) {
  const posthog = usePostHog();
  const [internalActiveTab, setInternalActiveTab] = useState<FunnelStage>(FunnelStage.UPPER_FUNNEL);

  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const setActiveTab = onTabChange || setInternalActiveTab;

  const isBrand = isBrandCampaign(campaign);
  const isLive = campaign.state === CAMPAIGN_STATE.LIVE;

  const hasMetadata = !isBrand && descriptions.some((d) => d.ad_funnel_stage);

  const funnelStages = Object.values(FunnelStage);

  // Add modal state (kept for adding new descriptions)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  // Tell-on-click validation (#5857): Save stays enabled; clicking it marks the
  // field inline ('Required' / 'No emojis' / duplicate) instead of toasting.
  const [modalError, setModalError] = useState<string | undefined>(undefined);

  // Auto-resize textarea
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
  }, []);
  useEffect(() => { if (isModalOpen) setTimeout(autoResize, 0); }, [isModalOpen, autoResize]);

  // Open add modal when triggered by parent
  useEffect(() => {
    if (showAddModal) {
      openAddModal();
      onAddModalClose?.();
    }
  }, [showAddModal]);

  const openAddModal = () => {
    setModalText('');
    setModalError(undefined);
    setIsModalOpen(true);
  };

  const handleModalSave = () => {
    if (!modalText.trim()) {
      setModalError('Required');
      return;
    }
    if (containsEmoji(modalText)) {
      setModalError('No emojis');
      return;
    }
    if (modalText.length > 90) {
      setModalError('Max 90 characters');
      return;
    }

    const trimmed = modalText.trim();
    const isDuplicate = descriptions.some(
      (d) => d.text === trimmed &&
              (hasMetadata ? d.ad_funnel_stage?.funnel === activeTab : true),
    );
    if (isDuplicate) {
      setModalError('Already exists in this stage');
      return;
    }

    posthog?.capture('ad_description_added', { campaign_id: campaign?.id });
    const newDesc: Description = {
      id: newTempDescriptionId(),
      text: trimmed,
      ...(hasMetadata ? { ad_funnel_stage: { funnel: activeTab } as AdFunnelStage } : {}),
    };
    const updated = [newDesc, ...descriptions];
    _setDescriptions(updated);
    setIsModalOpen(false);
  };

  // #4625-followup: ref-mirror state so changeDescription and the cell's
  // propagate() can read latest without the columns useMemo re-running on
  // every keystroke (which would remount DescriptionCell and steal focus).
  const descriptionsRef = useRef(descriptions);
  descriptionsRef.current = descriptions;
  const setDescriptionsRef = useRef(_setDescriptions);
  setDescriptionsRef.current = _setDescriptions;

  // #5857: returns null when the edit is accepted, or a short error message the cell
  // renders inline (red border + message where the counter sits) instead of a toast.
  const changeDescription = useCallback(async (descToChange: Description, value: string, originalText: string): Promise<string | null> => {
    if (originalText === value) return null;
    if (containsEmoji(value)) return 'No emojis';
    if (value.length > 90) return 'Max 90 characters';

    // Pre-check: reject within-funnel-stage duplicates before hitting the API.
    // Matches the DB constraint uix_description_ad_funnel_stage_id_text (NULLS NOT DISTINCT).
    const funnelStageId = descToChange.ad_funnel_stage?.id ?? null;
    const isDuplicate = descriptionsRef.current.some(
      (d) => d.id !== descToChange.id &&
              d.text === value &&
              (d.ad_funnel_stage?.id ?? null) === funnelStageId,
    );
    if (isDuplicate) return 'Duplicate';

    posthog?.capture('ad_description_edited', { campaign_id: campaign?.id });
    // No _setDescriptions here — keystroke propagation already updated state.
    return null;
    // descriptions / _setDescriptions read via refs; omit from deps to keep
    // the callback stable.
  }, [posthog, campaign?.id]);

  // Filter by id — every description gets a stable id (backend-assigned
  // positive for saved, ``newTempDescriptionId()``-assigned negative for
  // unsaved). The handleSaveAd payload strips negative ids before sending.
  const handleDelete = (description: Description) => {
    posthog?.capture('ad_description_deleted', { campaign_id: campaign?.id });
    setDescriptionsRef.current((prev) => prev.filter((d) => d.id !== description.id));
  };

  const metricsMap = useMemo(() => {
    const map = new Map<number, DescriptionWithMetrics>();
    if (metricsData?.descriptions) {
      for (const m of metricsData.descriptions) {
        map.set(m.description_id, m);
      }
    }
    return map;
  }, [metricsData]);

  const hasMetrics = metricsMap.size > 0;

  const activeMetricsItems = useMemo(() => {
    const items = Array.from(metricsMap.values());
    if (!hasMetadata) return items;
    const funnelIds = new Set(
      descriptions
        .filter((d) => d.ad_funnel_stage?.funnel === activeTab)
        .map((d) => d.id)
        .filter((id): id is number => id != null),
    );
    return items.filter((m) => funnelIds.has(m.description_id));
  }, [metricsMap, hasMetadata, descriptions, activeTab]);

  const topDescriptionId =
    activeMetricsItems.length > 0
      ? activeMetricsItems.reduce((best, m) => (m.impressions > best.impressions ? m : best)).description_id
      : null;

  const metricsLoadingRef = useRef(metricsLoading);
  metricsLoadingRef.current = metricsLoading;

  const buildRows = (descList: Description[]): DescriptionRow[] =>
    [...descList].reverse().map((description) => ({
      description,
      metrics: description.id ? metricsMap.get(description.id) : undefined,
      isTopPerformer: isLive && hasMetrics && description.id === topDescriptionId,
    }));

  const columns = useMemo((): ColumnDef<DescriptionRow, unknown>[] => [
    {
      accessorFn: (row) => row.description.text,
      id: 'description',
      header: 'Description',
      cell: function DescriptionCell({ row }: { row: { original: DescriptionRow } }) {
        const desc = row.original.description;
        const [localVal, setLocalVal] = useState(desc.text);
        // #5857: short inline message ('Required' / 'No emojis' / 'Duplicate') rendered
        // where the counter sits, replacing the old toast for commit-time validation.
        const [cellError, setCellError] = useState<string | null>(null);
        // #4625-followup: see HeadlineCell for the rationale on this ref.
        const originalTextRef = useRef(desc.text);

        const propagate = (next: string) => {
          setDescriptionsRef.current((prev) =>
            prev.map((d) => (d.id === desc.id ? { ...d, text: next } : d)),
          );
        };

        return (
          <div className="flex items-start gap-1">
            <textarea
              value={localVal}
              onChange={(e) => {
                // Google Ads descriptions are single-line (#5822): strip pasted
                // line breaks; Enter is blocked in onKeyDown.
                const next = e.target.value.replace(/[\r\n]+/g, ' ');
                setLocalVal(next);
                setCellError(null);
                propagate(next);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.preventDefault();
              }}
              onBlur={async () => {
                if (!localVal.trim()) {
                  setCellError('Required');
                  setLocalVal(originalTextRef.current);
                  propagate(originalTextRef.current);
                  setTimeout(() => setCellError(null), 5000);
                  return;
                }
                if (localVal !== originalTextRef.current) {
                  const error = await changeDescription(desc, localVal, originalTextRef.current);
                  if (error) {
                    setCellError(error);
                    setLocalVal(originalTextRef.current);
                    propagate(originalTextRef.current);
                    setTimeout(() => setCellError(null), 5000);
                  } else {
                    originalTextRef.current = localVal;
                  }
                }
              }}
              maxLength={90}
              disabled={disabled}
              rows={1}
              className={`text-sm text-gray-900 bg-transparent border rounded px-2 py-1.5 outline-none w-full transition-colors resize-none min-h-0 overflow-hidden ${cellError ? 'border-red-400 bg-red-50' : 'border-transparent hover:border-gray-300 focus:border-[#290BD9] focus:bg-white'}`}
              style={{ height: 'auto' }}
              ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
            />
            <span className={`text-xs shrink-0 mt-2 ${cellError ? 'text-red-500' : 'text-gray-400'}`}>{cellError ?? `${localVal.length}/90`}</span>
          </div>
        );
      },
      meta: { className: 'pl-[26px] pr-4 min-w-[320px]' },
    },
    {
      accessorFn: (row: DescriptionRow) => row.metrics?.performance_label ?? '',
      id: 'performanceBadge',
      header: 'Rating',
      cell: ({ row }: { row: { original: DescriptionRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return <PerformanceBadge label={metrics.performance_label} />;
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: DescriptionRow) => row.metrics?.impressions ?? 0,
      id: 'impressions',
      header: 'Imp.',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: DescriptionRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {formatCompactNumber(metrics.impressions)}
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: DescriptionRow) => row.metrics?.ctr ?? 0,
      id: 'ctr',
      header: 'CTR',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: DescriptionRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoadingRef.current && metrics) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {(metrics.ctr * 100).toFixed(1)}%
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    {
      accessorFn: (row: DescriptionRow) => row.metrics?.conversions ?? 0,
      id: 'conversions',
      header: 'Conv.',
      meta: { className: 'px-3' },
      cell: ({ row }: { row: { original: DescriptionRow } }) => {
        const metrics = row.original.metrics;
        if (!metricsLoading && metrics && metrics.conversions > 0) {
          return (
            <span className={cn('text-xs', row.original.isTopPerformer ? 'text-[#047857] font-medium' : 'text-gray-500')}>
              {formatCompactNumber(metrics.conversions)}
            </span>
          );
        }
        return <span className='text-xs text-gray-400'>-</span>;
      },
    },
    ...(!disabled ? [{
      id: 'actions',
      enableSorting: false,
      cell: ({ row }: { row: { original: DescriptionRow } }) => (
        <div className='flex justify-end'>
          <button
            onClick={() => handleDelete(row.original.description)}
            className='p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer'
          >
            <Trash2 className='h-4 w-4' />
          </button>
        </div>
      ),
    } satisfies ColumnDef<DescriptionRow, unknown>] : []),
  ], [disabled, changeDescription]);

  const buildSummaryFooter = (descList: Description[]) => {
    const ids = new Set(descList.map((d) => d.id).filter((id): id is number => id != null));
    const items = Array.from(metricsMap.values()).filter((m) => ids.has(m.description_id));
    if (items.length === 0) return undefined;
    const totalImpressions = items.reduce((s, m) => s + m.impressions, 0);
    const totalClicks = items.reduce((s, m) => s + m.clicks, 0);
    const totalConversions = items.reduce((s, m) => s + m.conversions, 0);
    const avgCtr = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
    const hasActions = !disabled;
    return (
      <tr>
        <td className='pl-[26px] pr-4 py-2.5' colSpan={2}>
          <span className='text-xs font-semibold text-gray-500'>All Descriptions</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{formatCompactNumber(totalImpressions)}</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{(avgCtr * 100).toFixed(1)}%</span>
        </td>
        <td className='px-3 py-2.5'>
          <span className='text-xs font-semibold text-gray-700'>{formatCompactNumber(totalConversions)}</span>
        </td>
        {hasActions && <td className='px-6 py-2.5' />}
      </tr>
    );
  };

  const renderTable = (descList: Description[]) => {
    const rows = buildRows(descList);
    return (
      <DataTable<DescriptionRow>
        data={rows}
        columns={columns}
        variant='flush'
        pageSize={999}
        footerContent={buildSummaryFooter(descList)}
        emptyIcon={AlignLeft}
        emptyTitle='No Descriptions'
        emptyDescription='Use the Add or Generate button above to add some.'
        // #4625-followup: stable row id by description.id; see
        // headline-management.tsx for the rationale.
        getRowId={(row) => String(row.description.id)}
      />
    );
  };

  return (
    <div className='space-y-4'>
      {hasMetadata ? (
        <Tabs
          value={activeTab}
          className='w-full'
          onValueChange={(value) => {
            const tab = value as FunnelStage;
            setActiveTab(tab);
            onTabChange?.(tab);
          }}
        >
          <TabsList className='grid w-full grid-cols-3'>
            {funnelStages.map((stage) => {
              const isPaused = funnelStageData?.find((fs) => fs.funnel === stage)?.is_paused;
              return (
                <TabsTrigger key={stage} value={stage} className='flex items-center gap-1.5'>
                  {getFunnelStageLabel(stage)}
                  {isPaused && <PauseCircle className='h-3.5 w-3.5 text-amber-500' />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {funnelStages.map((stage) => (
            <TabsContent key={stage} value={stage}>
              {renderTable(descriptions.filter((d) => d.ad_funnel_stage?.funnel === stage))}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        renderTable(descriptions)
      )}

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='sm:max-w-[400px]' aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Description</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 py-2'>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <Label>Description text</Label>
                <FieldError message={modalError} />
              </div>
              <Textarea
                ref={textareaRef}
                value={modalText}
                error={!!modalError}
                // Single-line only (#5822): strip pasted line breaks, block Enter.
                onChange={(e) => { setModalText(e.target.value.replace(/[\r\n]+/g, ' ')); setModalError(undefined); autoResize(); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                maxLength={90}
                placeholder='Enter description'
                autoFocus
                rows={1}
                className='resize-none min-h-[36px] overflow-hidden'
              />
              <p className='text-xs text-gray-500 text-right'>{modalText.length}/90</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsModalOpen(false)} className='leading-none'>
              Cancel
            </Button>
            <Button
              onClick={handleModalSave}
              // Tell-on-click (#5857): Save stays enabled; clicking it marks the field inline.
              className='bg-[#290BD9] hover:bg-[#2309C5] leading-none'
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
